import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiClockCounterClockwiseLight, PiLockSimpleLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { queryAuditLogs } from '../../../store/actions/AuditLogActions';
import GlassCard from '../../shared/brand/GlassCard';
import Button from '../../shared/TailwindComponents/Button';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import TablePagination from '../../shared/TailwindComponents/TablePagination';
import ActivityPane from '../../shared/activity/ActivityPane';
import { ActivityEntry, AuditRecord, toActivityEntries } from '../../shared/activity/activityModel';

export interface UserActivityPanelProps {
  /** The audit log is keyed by user uuid; the endpoint also accepts the id. */
  userUuid?: string;
  /** Legacy numeric id - used when the roster carries no uuid for this person. */
  userId?: number;
}

const PER_PAGE = 20;

/** Why the list is empty, when it is empty for a reason. */
type ActivityError = 'forbidden' | 'failed';

/**
 * What this person has been doing - their slice of the audit log, on the same
 * chronology surface the rest of the app uses (see `shared/activity`).
 *
 * `target_user_id` rather than `user_uuid`: this pane answers "what happened to
 * and around this account", which includes the admin actions someone ELSE took
 * on them. It is the same `/audit-logs` read the activity log page makes, one
 * page at a time, on the same pager - so a person with a thousand entries
 * behaves here the way they do there instead of stopping at the first twenty.
 */
/**
 * A person's feed mixes every kind of record, so a bare "Created" or "Set
 * Preferences" does not say what was acted on - it read as a broken table
 * (feedback round 3). Where the row has no description of its own, name the
 * kind of record it happened to. Entity-scoped panes do not need this (the
 * page already says which record), so it lives here, not in activityModel.
 */
const humaniseEntity = (type?: string): string =>
  (type || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

const withSubject = (entries: ActivityEntry[]): ActivityEntry[] =>
  entries.map((entry) => {
    if (entry.target) return entry;
    const subject = humaniseEntity((entry.raw as AuditRecord | undefined)?.entity_type);
    return subject ? { ...entry, target: subject } : entry;
  });

const UserActivityPanel: React.FC<UserActivityPanelProps> = ({ userUuid, userId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // A uuid whenever there is one; the numeric id is what old roster rows have.
  const targetId = userUuid || (userId != null ? String(userId) : '');

  const [loading, setLoading] = useState(!!targetId);
  const [error, setError] = useState<ActivityError | null>(null);

  const load = useCallback(
    async (pageNum: number) => {
      if (!targetId) {
        setEntries([]);
        setTotal(0);
        setTotalPages(0);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = (await dispatch(
          queryAuditLogs(
            {
              target_user_id: targetId,
              page: pageNum,
              per_page: PER_PAGE,
              sort_by: 'created_at',
              sort_order: 'desc',
            },
            { rethrow: true }
          ) as any
        )) as unknown as {
          data: AuditRecord[];
          total: number;
          total_pages: number;
        };
        setEntries(withSubject(toActivityEntries(result?.data || [])));
        setTotal(result?.total || 0);
        setTotalPages(result?.total_pages || 0);
      } catch (err: any) {
        // A refusal is a different answer from "nothing recorded", and the
        // reader has to be told which one they are looking at.
        setError(err?.response?.status === 403 ? 'forbidden' : 'failed');
        setEntries([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, targetId]
  );

  useEffect(() => { load(page); }, [load, page]);

  // Whose activity this is changed - page 3 of the last person is not page 3
  // of this one.
  useEffect(() => { setPage(1); }, [targetId]);

  /** What the footer says: the row RANGE on screen, not the page size. */
  const start = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const end = Math.min(page * PER_PAGE, total);

  const body = error === 'forbidden' ? (
    <EmptyState
      density="compact"
      icon={<PiLockSimpleLight />}
      headline={t("You don't have access to this activity")}
      subtext={t('Only an organization admin, or this person themselves, can see it.')}
    />
  ) : (
    <ActivityPane
      entries={entries}
      loading={loading}
      error={error === 'failed' ? t('Could not load this activity.') : ''}
      onRefresh={() => load(page)}
      density="pane"
      emptyState={
        targetId
          ? t('No activity recorded')
          : t('Activity is not available for this account.')
      }
    />
  );

  return (
    <GlassCard
      icon={<PiClockCounterClockwiseLight />}
      title={t('Activity')}
      subtitle={t('Everything recorded for this person, newest first.')}
      className="flex min-h-0 flex-col"
      bodyClassName="min-h-0 flex-1 overflow-hidden"
    >
      <div className="max-h-[28rem] min-h-0">{body}</div>

      {!error && total > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          <span className="px-3 text-xs text-fg-muted">
            {t('Showing')} {start}&ndash;{end} {t('of')} {total} {t('entries')}
          </span>
          {totalPages > 1 && (
            <TablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              disabled={loading}
            />
          )}
        </div>
      )}

      {error === 'failed' && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="small" onClick={() => load(page)}>
            {t('Retry')}
          </Button>
        </div>
      )}
    </GlassCard>
  );
};

export default UserActivityPanel;
